import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
// react component for creating dynamic tables
import ReactTable from 'react-table';

// @material-ui/icons
import Add from '@material-ui/icons/Add';

import { USER_LIST_NAMES } from '#constants';

// core components
import Button from '../../../components/CustomButtons/Button';
import CardBody from '../../../components/Card/CardBody';
import FilterInput from '../../../components/FilterInput';
import Snackbar from '../../../components/Snackbar/Snackbar';
import CustomSelect from '../../../components/CustomInput/CustomSelect';

import * as actions from '../../../reducers/Users/actions';
import * as areasActions from '../../../reducers/Areas/actions';

import UserDialog from './components/UserDialog';
import { ModalCRUD, Details, Text } from './components/index';
import { parseUserRoles } from './components/ModalCRUD/components/UserInfo/parser';

import {
  ButtonContainer, CardWrapper, Container, NoDataWrapper,
} from './style';
import NoDataImg from '../../../assets/img/no-data.svg';

let timeout;

const isValidPage = (page, pageSize, total) => {
  const pages = Math.ceil(total / pageSize);
  const isValid = !Number.isNaN(page) && page <= pages;
  return isValid;
};

const NoData = () => (
  <NoDataWrapper id="noData">
    <img src={NoDataImg} height="84px" alt="no data" />
    <h4>No data</h4>
    <p>Change the dates range or adjust the filters.</p>
  </NoDataWrapper>
);

const formatDataList = data => data.map(
  ({ role, ...other }) => ({
    ...other,
    role: USER_LIST_NAMES[role] || role,
  })
);
class Users extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeDialog: false,
      user        : {},
      pageSize    : 20,
    };
  }

  componentDidMount() {
    const { areasFetch } = this.props;
    areasFetch({});
  }

  onFetchData = ({
    filtered, page, pageSize, sorted,
  }) => {
    const { users, filterFetchTimeout, usersFetch } = this.props;
    const { pageSize: pageSizeState } = this.state;
    const isValid = isValidPage(page, pageSizeState, users.data.total || 0);
    if (isValid) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        this.setState(() => ({ pageSize }));
        usersFetch({
          filtered,
          page,
          pageSize,
          sorted,
        });
      }, filterFetchTimeout);
    }
  };

  toggleDialog = () => {
    this.setState(prevState => ({ activeDialog: !prevState.activeDialog, user: {} }));
  };

  onDetailsClick = (user) => {
    this.setState({ activeDialog: true, user });
  };

  cleanUserNotification = () => {
    const { cleanNotification } = this.props;
    cleanNotification();
  };

  render() {
    const {
      users, total, defaultPageSize, user: { data: loggedUser },
    } = this.props;
    const { activeDialog, user } = this.state;

    return (
      <>
        <Container>
          <CardWrapper>
            <CardBody>
              {/* https://github.com/tanstack/react-table/tree/v6 */}
              <ReactTable
                data={users.data.users} // users.data also contains pagination params
                resolveData={formatDataList}
                defaultPageSize={defaultPageSize}
                filterable
                manual
                columns={[
                  {
                    Header  : 'Name',
                    accessor: 'name',
                    Cell    : Text,
                    minWidth: 160,
                    Filter  : props => <FilterInput placeholder="Name" {...props} />,
                  },
                  {
                    Header  : 'Email',
                    accessor: 'email',
                    Cell    : Text,
                    minWidth: 160,
                    Filter  : props => <FilterInput placeholder="example@email.com" data-testid="input-filter-user-email" {...props} />,
                  },
                  {
                    Header  : 'Role',
                    accessor: 'role',
                    Cell    : Text,
                    minWidth: 160,
                    Filter  : props => (
                      <CustomSelect
                        placeholder="Role"
                        data-testid="input-filter-user-role"
                        inputProps={{
                          'aria-label' : 'Role',
                          onChange     : event => props.onChange(event.target.value),
                          'data-testid': 'input-filter-user-role',
                        }}
                        formControlProps={{
                          fullWidth: true,
                          style    : {
                            paddingTop: '15px',
                          },
                        }}
                        options={parseUserRoles(loggedUser.role)}
                        {...props}
                      />
                    ),
                  },
                  {
                    Header    : '',
                    accessor  : 'actions',
                    sortable  : false,
                    filterable: false,
                    Cell      : props => (
                      <Details
                        onDetailsClick={this.onDetailsClick}
                        {...props}
                      />
                    ),
                  },
                ]}
                onFetchData={this.onFetchData}
                minRows={0}
                showPaginationTop={false}
                showPaginationBottom={total !== 0}
                NoDataComponent={NoData}
              />
            </CardBody>
            <ButtonContainer>
              <Button onClick={this.toggleDialog} color="info" data-testid="button-new-user">
                <Add />
                {' '}
                NEW USER
              </Button>
            </ButtonContainer>
          </CardWrapper>
        </Container>
        <ModalCRUD open={activeDialog} onClose={this.toggleDialog} user={user} />
        {/* BACKUP LINE IN CASE OF ROLLBACK. This could be an example of use Feature Flags */}
        {/* <UserDialog open={activeDialog} onClose={this.toggleDialog} user={user} /> */}
        {users.notification.message && (
          <Snackbar
            color={users.notification.error ? 'danger' : 'success'}
            message={users.notification.message}
            open={Object.keys(users.notification).length}
            close
            closeNotification={this.cleanUserNotification}
          />
        )}
      </>
    );
  }
}

Users.propTypes = {
  usersFetch       : PropTypes.func.isRequired,
  cleanNotification: PropTypes.func.isRequired,
  areasFetch       : PropTypes.func.isRequired,
  users            : PropTypes.shape({
    data        : PropTypes.array,
    notification: PropTypes.shape({
      error  : PropTypes.bool,
      message: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({
    data: PropTypes.shape({}),
  }),
  total             : PropTypes.number,
  defaultPageSize   : PropTypes.number,
  filterFetchTimeout: PropTypes.number,
};

Users.defaultProps = {
  users             : {},
  user              : {},
  total             : 0,
  defaultPageSize   : 20,
  filterFetchTimeout: 350,
};

const mapStateToProps = ({ users, user }) => ({
  users,
  user,
});

const mapDispatchToProps = dispatch => ({
  usersFetch       : params => dispatch(actions.usersFetch(params)),
  cleanNotification: () => dispatch(actions.cleanNotification()),
  areasFetch       : props => dispatch(areasActions.areasFetch(props)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(Users);
